import { Fragment, useMemo } from "react";

import { highlightParts } from "@/lib/faqSearch";
import { cn } from "@/lib/utils/cn";

/** Wraps matches of the search terms in <mark>. */
export function Highlight({ text, terms }: { text: string; terms: string[] }) {
  return (
    <>
      {highlightParts(text, terms).map(([chunk, hit], i) =>
        hit ? (
          <mark key={i} className="rounded-sm bg-primary-100 px-0.5 text-primary-800 dark:bg-primary-500/25 dark:text-primary-200">
            {chunk}
          </mark>
        ) : (
          <Fragment key={i}>{chunk}</Fragment>
        ),
      )}
    </>
  );
}

type Block = { kind: "p"; text: string } | { kind: "ul"; items: string[] };

const BULLET = /^\s*[•\-*]\s+/;
// Sentence end followed by a capital/quote — but not after "e.g." / "i.e." / "vs."
const SENTENCE_SPLIT = /(?<=[.!?؟])(?<!\be\.g\.)(?<!\bi\.e\.)(?<!\bvs\.)\s+(?=[A-Z“"«؀-ۿ•])/;

/** Splits long paragraphs into short ones (~2 sentences) so answers read as steps, not a wall of text. */
function chunkParagraph(text: string, max = 230): string[] {
  if (text.length <= max) return [text];
  const sentences = text.split(SENTENCE_SPLIT).filter(Boolean);
  if (sentences.length < 3) return [text];
  const out: string[] = [];
  let cur = "";
  for (const s of sentences) {
    if (cur && (cur + " " + s).length > max) {
      out.push(cur);
      cur = s;
    } else {
      cur = cur ? `${cur} ${s}` : s;
    }
  }
  if (cur) out.push(cur);
  return out;
}

function toBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let list: string[] | null = null;
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    if (BULLET.test(line)) {
      (list ??= []).push(line.replace(BULLET, ""));
      continue;
    }
    if (list) {
      blocks.push({ kind: "ul", items: list });
      list = null;
    }
    for (const chunk of chunkParagraph(line)) blocks.push({ kind: "p", text: chunk });
  }
  if (list) blocks.push({ kind: "ul", items: list });
  return blocks;
}

// “Label”, "Label" or «Label» — button/field names (short) vs. quoted messages (long).
const QUOTED = /(“[^”]+”|"[^"]+"|«[^»]+»)/g;

function Inline({ text, terms }: { text: string; terms: string[] }) {
  return (
    <>
      {text.split(QUOTED).map((part, i) => {
        if (!part) return null;
        const quoted = /^(“[^”]+”|"[^"]+"|«[^»]+»)$/.test(part);
        if (!quoted) return <Highlight key={i} text={part} terms={terms} />;
        const inner = part.slice(1, -1);
        // Short quotes are UI labels -> small tag. Long quotes are messages -> italic, quotes kept.
        return inner.length <= 48 ? (
          <span
            key={i}
            className="mx-0.5 inline-block whitespace-normal rounded-md border border-primary-200/60 bg-primary-50 px-1.5 py-px text-[0.92em] font-medium leading-snug text-primary-800 dark:border-primary-500/25 dark:bg-primary-500/15 dark:text-primary-200"
          >
            <Highlight text={inner} terms={terms} />
          </span>
        ) : (
          <em key={i} className="text-ink-900">
            <Highlight text={part} terms={terms} />
          </em>
        );
      })}
    </>
  );
}

/** Formatted FAQ answer: short paragraphs, bullet lists, and UI labels shown as tags. */
export function FaqAnswer({ text, terms = [], className }: { text: string; terms?: string[]; className?: string }) {
  const blocks = useMemo(() => toBlocks(text), [text]);
  return (
    <div className={cn("space-y-3 text-[13.5px] leading-[1.7] text-ink-700", className)}>
      {blocks.map((b, i) =>
        b.kind === "p" ? (
          <p key={i}>
            <Inline text={b.text} terms={terms} />
          </p>
        ) : (
          <ul key={i} className="space-y-2 rounded-lg bg-white/60 p-3 dark:bg-white/[0.04]">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2.5">
                <span className="mt-[0.62em] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-400" aria-hidden="true" />
                <span className="min-w-0">
                  <Inline text={it} terms={terms} />
                </span>
              </li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}
